/**
 * Unlock conditions, shared by upgrades (when they appear in the shop) and
 * achievements (when they fire). Pure: evaluated against state and the
 * current EPS, which the caller already has in hand.
 */

import { DINOS, DINOS_BY_ID, aOrAn, availableDinos } from '../data/dinos';
import type { SpliceGrade } from '../data/dinos';
import { ERA_MODIFIERS, EXPEDITIONS_BY_ID } from '../data/eras';
import { METEOR_KINDS, METEOR_KINDS_BY_ID } from '../data/meteors';
import { PERKS } from '../data/museum';
import type { GameState } from './state';
import { formatDuration, formatEggs, formatRate } from './numbers';
// economy imports this module too; the cycle is safe because offlineRate is
// only called inside a function, never while either module is loading.
import { offlineRate } from './economy';

/** One-off moments that happen in the view and are recorded in `state.feats`. */
export type Feat =
  /** Every rock of a meteor shower broken, none left to cool. */
  | 'shower'
  /** A meteor broken with under a second left on its countdown. */
  | 'last-second'
  /** Time away collected in full up to the cap. */
  | 'long-nap'
  /** The Rules panel kept open long enough to read some of it. */
  | 'rules';

export type Condition =
  | { kind: 'totalEggs'; amount: number }
  /**
   * Eggs laid in the current era. The upgrades unlock on this, so each era
   * reveals them one at a time again rather than opening with every one
   * ever seen, unaffordable. It changes nothing else: each costs 2-5x its
   * unlock, so it could never be afforded before it unlocked this era anyway.
   */
  | { kind: 'eggsThisEra'; amount: number }
  | { kind: 'clicks'; count: number }
  | { kind: 'eps'; amount: number }
  | { kind: 'dinoLevel'; dinoId: string; level: number }
  | { kind: 'cracks'; count: number }
  /**
   * At least this many dinosaurs have a pet name: now, or with `ever`, across
   * every era played (the "every dinosaur, ever" achievements).
   */
  | { kind: 'named'; count: number; ever?: boolean }
  /** Some dinosaur's pet name is its own species name. */
  | { kind: 'namesake' }
  | { kind: 'meteorsBroken'; count: number }
  /** Meteors of one kind broken. */
  | { kind: 'meteorKindBroken'; kindId: string; count: number }
  | { kind: 'shardsEarned'; amount: number }
  /** Each of these dinosaurs hatched at some point, in any era (the residents). */
  | { kind: 'hatchedEver'; dinoIds: readonly string[] }
  /** Dinosaurs evolved: now, or with `ever`, across every era played. */
  | { kind: 'evolved'; count: number; ever?: boolean }
  /** An evolved dinosaur that the player had named. */
  | { kind: 'evolvedNamed' }
  /** Eras ended. */
  | { kind: 'eras'; count: number }
  /** Fossils earned, lifetime. */
  | { kind: 'fossilsEarned'; count: number }
  /** The same dinosaur excavated this many eras running. */
  | { kind: 'excavatedStreak'; count: number }
  /** An era ended with nothing excavated: the whole herd left in the ground. */
  | { kind: 'leftBuried' }
  /** An expedition seen through to its extinction. */
  | { kind: 'expeditionDone' }
  /** A single era that laid this much. */
  | { kind: 'eraEggs'; amount: number }
  /** A share link used, from the Share panel. */
  | { kind: 'shared' }
  /** An era played under each era modifier (the current one counts). */
  | { kind: 'allEraModifiers' }
  /** An expedition of this kind seen through to its extinction. */
  | { kind: 'expeditionFinished'; tripId: string }
  /** A Sprint that laid its required eggs, so it paid the triple fossils. */
  | { kind: 'sprintWon' }
  /** An era ended within this many seconds of starting. */
  | { kind: 'quickEra'; seconds: number }
  /** An era that ran at least this many seconds before it ended. */
  | { kind: 'longEra'; seconds: number }
  /** At least this many fossils from a single extinction. */
  | { kind: 'eraFossils'; count: number }
  /** A dinosaur excavated that had a pet name. */
  | { kind: 'excavatedNamed' }
  /** This many different tiers on show in the Museum's Collection. */
  | { kind: 'onShow'; count: number }
  /** Every Museum perk bought. */
  | { kind: 'allPerks' }
  /** Every dinosaur at this level or higher at once (1: all hatched). */
  | { kind: 'herdAtLevel'; level: number }
  /** Each of these dinosaurs at this level or higher. */
  | { kind: 'dinosAtLevel'; dinoIds: readonly string[]; level: number }
  /** This dinosaur hatched within this many seconds of the era starting. */
  | { kind: 'fastHatch'; dinoId: string; seconds: number }
  /** A production boost and a click boost running at once. */
  | { kind: 'doubleBoost' }
  /** A golden egg of this bonus caught. */
  | { kind: 'bonusCaught'; bonusId: string }
  /** One meteor of every kind broken. */
  | { kind: 'allMeteorKinds' }
  /** This many shards in hand at once. */
  | { kind: 'shardsHeld'; amount: number }
  /** This shard-shop item owned. */
  | { kind: 'ownsShopItem'; itemId: string }
  /** A dinosaur that needs this grade of splice evolved (past a weaker one's reach). */
  | { kind: 'evolvedNeeding'; splice: SpliceGrade }
  /** The away rate at or above this fraction. */
  | { kind: 'awayRate'; rate: number }
  /** A one-off moment recorded by the view. */
  | { kind: 'feat'; feat: Feat };

/** How many species a lifetime record and this era hold between them. */
function ever(record: readonly string[], now: readonly string[]): number {
  return new Set([...record, ...now]).size;
}

export function isConditionMet(cond: Condition, state: GameState, eps: number): boolean {
  switch (cond.kind) {
    case 'totalEggs':
      return state.totalEggs >= cond.amount;
    case 'eggsThisEra':
      return state.eggsThisEra >= cond.amount;
    case 'clicks':
      return state.clicks >= cond.count;
    case 'eps':
      return eps >= cond.amount;
    case 'dinoLevel':
      return (state.dinos[cond.dinoId] ?? 0) >= cond.level;
    case 'cracks':
      return state.cracks >= cond.count;
    case 'named':
      return (
        (cond.ever
          ? ever(state.everNamed, Object.keys(state.names))
          : Object.keys(state.names).length) >= cond.count
      );
    case 'namesake':
      // Its species as the card shows it, base or evolved, and a pack
      // counts by the animal's name ("Velociraptor", not "Velociraptor pack").
      return DINOS.some((d) => {
        const given = (state.names[d.id] ?? '').trim().toLowerCase();
        if (given === '') return false;
        return [d.name, d.evolved.name]
          .flatMap((n) => [n, n.replace(/ pack$/i, '')])
          .some((n) => n.toLowerCase() === given);
      });
    case 'meteorsBroken':
      return state.meteorsBroken >= cond.count;
    case 'meteorKindBroken':
      return (state.meteorKindsBroken[cond.kindId] ?? 0) >= cond.count;
    case 'shardsEarned':
      return state.shardsEarned >= cond.amount;
    case 'evolved':
      return (
        (cond.ever ? ever(state.everEvolved, state.evolved) : state.evolved.length) >= cond.count
      );
    case 'hatchedEver':
      return cond.dinoIds.every(
        (id) => state.everHatched.includes(id) || (state.dinos[id] ?? 0) > 0,
      );
    case 'evolvedNamed':
      return state.evolved.some((id) => (state.names[id] ?? '').trim() !== '');
    case 'eras':
      return state.era >= cond.count;
    case 'fossilsEarned':
      return state.fossilsEarned >= cond.count;
    case 'excavatedStreak': {
      // The last N logged eras, all keeping the same dinosaur.
      const recent = state.eraLog.slice(-cond.count);
      if (recent.length < cond.count) return false;
      const first = recent[0]?.excavated;
      return first !== null && first !== undefined && recent.every((r) => r.excavated === first);
    }
    case 'leftBuried':
      return state.eraLog.some((r) => r.excavated === null);
    case 'expeditionDone':
      return state.eraLog.some((r) => r.expedition !== null);
    case 'eraEggs':
      return state.eggsThisEra >= cond.amount || state.eraLog.some((r) => r.eggs >= cond.amount);
    case 'shared':
      return state.shared;
    case 'allEraModifiers':
      return ERA_MODIFIERS.every(
        (m) => state.eraId === m.id || state.eraLog.some((r) => r.eraId === m.id),
      );
    case 'expeditionFinished':
      return state.eraLog.some((r) => r.expedition === cond.tripId);
    case 'sprintWon': {
      const need = EXPEDITIONS_BY_ID.get('trip:sprint')?.requiresEggs ?? Infinity;
      return state.eraLog.some((r) => r.expedition === 'trip:sprint' && r.eggs >= need);
    }
    case 'quickEra':
      return state.eraLog.some((r) => r.seconds <= cond.seconds);
    case 'longEra':
      return state.eraLog.some((r) => r.seconds >= cond.seconds);
    case 'eraFossils':
      return state.eraLog.some((r) => r.fossils >= cond.count);
    case 'excavatedNamed':
      return state.eraLog.some((r) => r.excavated !== null && r.excavatedName !== null);
    case 'onShow':
      return (
        new Set(state.eraLog.map((r) => r.excavated).filter((id) => id !== null)).size >= cond.count
      );
    case 'allPerks':
      return PERKS.every((p) => state.museum.includes(p.id));
    case 'herdAtLevel':
      return availableDinos(state.eraId).every((d) => (state.dinos[d.id] ?? 0) >= cond.level);
    case 'dinosAtLevel':
      return cond.dinoIds.every((id) => (state.dinos[id] ?? 0) >= cond.level);
    case 'fastHatch':
      // Checked every tick, so it fires the moment the tier hatches, while
      // the clock still says how long the era has run.
      return (
        (state.dinos[cond.dinoId] ?? 0) > 0 &&
        (Date.now() - state.eraStartedAt) / 1000 <= cond.seconds
      );
    case 'doubleBoost':
      return state.boost !== null && state.clickBoost !== null;
    case 'bonusCaught':
      return (state.bonusesCaught[cond.bonusId] ?? 0) > 0;
    case 'allMeteorKinds':
      return METEOR_KINDS.every((k) => (state.meteorKindsBroken[k.id] ?? 0) > 0);
    case 'shardsHeld':
      return state.shards >= cond.amount;
    case 'ownsShopItem':
      return state.shop.includes(cond.itemId);
    case 'evolvedNeeding':
      return state.evolved.some((id) => DINOS_BY_ID.get(id)?.splice === cond.splice);
    case 'awayRate':
      return offlineRate(state) >= cond.rate - 1e-9;
    case 'feat':
      return state.feats.includes(cond.feat);
  }
}

function dinoName(id: string): string {
  return DINOS.find((d) => d.id === id)?.name ?? id;
}

/** Human-readable form for tooltips: "Raise Gallimimus to level 10". */
export function describeCondition(cond: Condition): string {
  switch (cond.kind) {
    case 'totalEggs':
      return `Collect ${formatEggs(cond.amount)} eggs`;
    case 'eggsThisEra':
      return `Lay ${formatEggs(cond.amount)} eggs this era`;
    case 'clicks':
      return `Click the egg ${formatEggs(cond.count)} times`;
    case 'eps':
      return `Reach ${formatRate(cond.amount)} ${cond.amount === 1 ? 'egg' : 'eggs'} per second`;
    case 'dinoLevel':
      return cond.level === 1
        ? `Hatch ${aOrAn(dinoName(cond.dinoId))} ${dinoName(cond.dinoId)}`
        : `Raise ${dinoName(cond.dinoId)} to level ${cond.level}`;
    case 'cracks':
      return cond.count === 1 ? 'Catch a falling egg' : `Catch ${cond.count} falling eggs`;
    case 'named':
      if (cond.count === 1) return 'Name a dinosaur';
      if (cond.count >= DINOS.length)
        return cond.ever ? 'Name every dinosaur, ever' : 'Name every dinosaur';
      return `Name ${cond.count} dinosaurs`;
    case 'namesake':
      return 'Name a dinosaur after its own species';
    case 'meteorsBroken':
      return cond.count === 1 ? 'Break a meteor' : `Break ${cond.count} meteors`;
    case 'meteorKindBroken': {
      const name = METEOR_KINDS_BY_ID.get(cond.kindId)?.name ?? cond.kindId;
      return cond.count === 1 ? `Break a ${name} meteor` : `Break ${cond.count} ${name} meteors`;
    }
    case 'shardsEarned':
      return `Earn ${cond.amount} meteor shards`;
    case 'evolved':
      if (cond.count === 1) return 'Evolve a dinosaur';
      if (cond.count >= DINOS.length) {
        return cond.ever ? 'Evolve every dinosaur, ever' : 'Evolve every dinosaur';
      }
      return `Evolve ${cond.count} dinosaurs`;
    case 'hatchedEver':
      return "Hatch every era's resident, across your eras";
    case 'evolvedNamed':
      return 'Evolve a dinosaur you named';
    case 'eras':
      return cond.count === 1 ? 'End an era' : `End ${cond.count} eras`;
    case 'fossilsEarned':
      return `Earn ${cond.count} fossils`;
    case 'excavatedStreak':
      return `Excavate the same dinosaur ${cond.count} eras running`;
    case 'leftBuried':
      return 'End an era without excavating a fossil';
    case 'expeditionDone':
      return 'Finish an expedition';
    case 'eraEggs':
      return `Lay ${formatEggs(cond.amount)} eggs in a single era`;
    case 'shared':
      return 'Share DinoClicker';
    case 'allEraModifiers':
      return 'Play an era of every kind';
    case 'expeditionFinished':
      return `Finish a ${EXPEDITIONS_BY_ID.get(cond.tripId)?.name ?? cond.tripId} expedition`;
    case 'sprintWon':
      return 'Win the triple fossils on a Sprint';
    case 'quickEra':
      return `End an era within ${formatDuration(cond.seconds)} of it starting`;
    case 'longEra':
      return `Let an era run for ${formatDuration(cond.seconds)}`;
    case 'eraFossils':
      return `Earn ${cond.count} fossils from one extinction`;
    case 'excavatedNamed':
      return 'Excavate a dinosaur you named';
    case 'onShow':
      return `Put ${cond.count} dinosaurs on show in the Museum`;
    case 'allPerks':
      return 'Buy every Museum perk';
    case 'herdAtLevel':
      return cond.level === 1
        ? 'Hatch every available dinosaur at once'
        : `Raise every available dinosaur to level ${cond.level}`;
    case 'dinosAtLevel':
      return `Raise ${cond.dinoIds.map(dinoName).join(' and ')} to level ${cond.level}`;
    case 'fastHatch': {
      const name = dinoName(cond.dinoId);
      return `Hatch ${aOrAn(name)} ${name} within ${formatDuration(cond.seconds)} of an era starting`;
    }
    case 'doubleBoost':
      return 'Run a production boost and a click boost at once';
    case 'bonusCaught':
      return 'Catch a rare golden egg';
    case 'allMeteorKinds':
      return 'Break a meteor of every kind';
    case 'shardsHeld':
      return `Hold ${cond.amount} shards at once`;
    case 'ownsShopItem':
      return 'Buy a shard-shop item';
    case 'evolvedNeeding': {
      const name = DINOS.find((d) => d.splice === cond.splice)?.name ?? 'dinosaur';
      return `Evolve ${aOrAn(name)} ${name} or bigger`;
    }
    case 'awayRate':
      return `Reach an away rate of ${Math.round(cond.rate * 100)}%`;
    case 'feat':
      return 'A moment to remember';
  }
}
